import { z } from 'zod';
import i18n from '../i18n';

// Campi indirizzo FatturaPA (cessionario): vuoto ammesso lato form, ma se valorizzati
// CAP=5 cifre, provincia/nazione=2 lettere, codice destinatario=7 caratteri.
const cap = z.string().refine((v) => !v || /^\d{5}$/.test(v), { message: i18n.t('anagrafiche.val_cliente.cap') }).optional().or(z.literal(''));
const provincia = z.string().refine((v) => !v || /^[A-Za-z]{2}$/.test(v), { message: i18n.t('anagrafiche.val_cliente.provincia') }).optional().or(z.literal(''));
const nazione = z.string().refine((v) => !v || /^[A-Za-z]{2}$/.test(v), { message: i18n.t('anagrafiche.val_cliente.nazione') }).optional().or(z.literal(''));
const codiceDest = z.string().refine((v) => !v || /^[A-Za-z0-9]{7}$/.test(v), { message: i18n.t('anagrafiche.val_cliente.codice_dest') }).optional().or(z.literal(''));
const piva = z.string().refine((v) => !v || /^\d{11}$/.test(v), { message: i18n.t('anagrafiche.val_cliente.piva') }).optional().or(z.literal(''));
// CF: 16 caratteri (persona fisica) oppure 11 cifre (ente senza P.IVA — ONLUS,
// PA, condomini). La P.IVA resta nel suo campo: gli 11 cifre servono a chi non
// ce l'ha. Il checksum lo verifica il backend, sorgente unica.
const cf = z
  .string()
  .refine((v) => !v || v.length === 16 || /^\d{11}$/.test(v), { message: i18n.t('anagrafiche.val_cliente.cf') })
  .optional()
  .or(z.literal(''));

export const clienteCreateSchema = z
  .object({
    ragione_sociale: z.string().min(1, i18n.t('anagrafiche.val_cliente.ragione_sociale')),
    piva,
    codice_fiscale: cf,
    indirizzo: z.string().optional().or(z.literal('')),
    cap,
    citta: z.string().optional().or(z.literal('')),
    provincia,
    nazione,
    codice_destinatario: codiceDest,
    referente: z.string().optional().or(z.literal('')),
    telefono: z.string().optional().or(z.literal('')),
    email: z.string().email(i18n.t('anagrafiche.val_cliente.email')),
    pec: z.string().email(i18n.t('anagrafiche.val_cliente.pec')).optional().or(z.literal('')),
  })
  .refine((d) => !!(d.piva?.trim() || d.codice_fiscale?.trim()), {
    message: i18n.t('anagrafiche.val_cliente.piva_o_cf'),
    path: ['piva'],
  })
  // R5/F2 — solo in creazione: azienda (P.IVA valorizzata) ⇒ codice destinatario
  // reale (≠0000000) OPPURE PEC. Privati (solo CF) esenti. Specular al BE.
  .refine((d) => {
    if (!d.piva?.trim()) return true;
    const cod = d.codice_destinatario?.trim() ?? '';
    return (cod !== '' && cod !== '0000000') || !!d.pec?.trim();
  }, {
    message: i18n.t('anagrafiche.val_cliente.azienda_codice_o_pec'),
    path: ['codice_destinatario'],
  });

export type ClienteCreateData = z.infer<typeof clienteCreateSchema>;

export const clienteUpdateSchema = z.object({
  ragione_sociale: z.string().min(1).optional(),
  piva,
  codice_fiscale: cf,
  indirizzo: z.string().optional().or(z.literal('')),
  cap,
  citta: z.string().optional().or(z.literal('')),
  provincia,
  nazione,
  codice_destinatario: codiceDest,
  referente: z.string().optional().or(z.literal('')),
  telefono: z.string().optional().or(z.literal('')),
  email: z.string().email(i18n.t('anagrafiche.val_cliente.email')).optional().or(z.literal('')),
  pec: z.string().email(i18n.t('anagrafiche.val_cliente.pec')).optional().or(z.literal('')),
});

export type ClienteUpdateData = z.infer<typeof clienteUpdateSchema>;
